import { normalizedHpDisplay, unitTotalHp, type Unit } from "@dailywars/core/engine";

import type { CanvasBoardAnimation } from "#/game/animation/types";
import { getHpBadgeImage, isHpBadgeValue } from "#/game/badgeSprites";
import { isImageReady } from "#/game/imageReady";
import { getSpriteUnitFrame, type SpriteUnitFrame } from "#/game/unitSprites";

import { TILE_SIZE } from "./boardConstants";
import type { UnitDrawLayout } from "./boardUnitGeometry";

const CARGO_BADGE_SIZE = 16;
const CARGO_BADGE_RADIUS = 8;
const CARGO_BADGE_INSET = 1;

function drawBadgeOverlay({
  ctx,
  img,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  img: HTMLImageElement;
  layout: UnitDrawLayout;
}): void {
  if (!isImageReady(img)) return;
  ctx.drawImage(img, layout.drawX, layout.drawY, TILE_SIZE, TILE_SIZE);
}

function unitForHpBadge({
  unit,
  animation,
}: {
  unit: Unit;
  animation: CanvasBoardAnimation | null;
}): Unit {
  if (animation?.type !== "move" && animation?.type !== "attackFx") return unit;
  const override = animation.hpDisplayUnits?.find((u) => u.id === unit.id);
  return override ?? unit;
}

function drawUnitHpBadge({
  ctx,
  unit,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
}): void {
  const hpDisplay = normalizedHpDisplay(unitTotalHp(unit));
  if (!isHpBadgeValue(hpDisplay)) return;
  drawBadgeOverlay({ ctx, img: getHpBadgeImage(hpDisplay), layout });
}

function cargoBadgeOrigin(layout: UnitDrawLayout): { x: number; y: number } {
  return {
    x: layout.drawX + CARGO_BADGE_INSET,
    y: layout.drawY + CARGO_BADGE_INSET,
  };
}

function drawCargoBadgeSprite({
  ctx,
  frame,
  origin,
}: {
  ctx: CanvasRenderingContext2D;
  frame: SpriteUnitFrame;
  origin: { x: number; y: number };
}): void {
  if (frame.flipX) {
    ctx.translate(origin.x + CARGO_BADGE_SIZE, origin.y);
    ctx.scale(-1, 1);
    ctx.drawImage(
      frame.img,
      frame.sx,
      frame.sy,
      frame.sw,
      frame.sh,
      0,
      0,
      CARGO_BADGE_SIZE,
      CARGO_BADGE_SIZE,
    );
    return;
  }
  ctx.drawImage(
    frame.img,
    frame.sx,
    frame.sy,
    frame.sw,
    frame.sh,
    origin.x,
    origin.y,
    CARGO_BADGE_SIZE,
    CARGO_BADGE_SIZE,
  );
}

function cargoBadgeCenter(origin: { x: number; y: number }): { x: number; y: number } {
  return {
    x: origin.x + CARGO_BADGE_SIZE / 2,
    y: origin.y + CARGO_BADGE_SIZE / 2,
  };
}

function traceCargoBadgeCircle(
  ctx: CanvasRenderingContext2D,
  center: { x: number; y: number },
): void {
  ctx.beginPath();
  ctx.arc(center.x, center.y, CARGO_BADGE_RADIUS, 0, Math.PI * 2);
}

function fillCargoBadgeCircle({
  ctx,
  center,
}: {
  ctx: CanvasRenderingContext2D;
  center: { x: number; y: number };
}): void {
  traceCargoBadgeCircle(ctx, center);
  ctx.fillStyle = "rgb(179, 212, 253)";
  ctx.fill();
}

function strokeCargoBadgeCircle({
  ctx,
  center,
}: {
  ctx: CanvasRenderingContext2D;
  center: { x: number; y: number };
}): void {
  traceCargoBadgeCircle(ctx, center);
  ctx.strokeStyle = "#000000";
  ctx.lineWidth = 2;
  ctx.stroke();
}

function drawUnitCargoBadge({
  ctx,
  unit,
  layout,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
}): void {
  if (!unit.cargo) return;
  const frame = getSpriteUnitFrame({
    kind: unit.cargo.kind,
    owner: unit.owner,
    facing: layout.facing,
  });
  if (!frame) return;

  const origin = cargoBadgeOrigin(layout);
  const center = cargoBadgeCenter(origin);
  ctx.save();
  ctx.imageSmoothingEnabled = false;
  fillCargoBadgeCircle({ ctx, center });
  ctx.save();
  traceCargoBadgeCircle(ctx, center);
  ctx.clip();
  drawCargoBadgeSprite({ ctx, frame, origin });
  ctx.restore();
  strokeCargoBadgeCircle({ ctx, center });
  ctx.restore();
}

export function drawUnitBadges({
  ctx,
  unit,
  layout,
  animation,
}: {
  ctx: CanvasRenderingContext2D;
  unit: Unit;
  layout: UnitDrawLayout;
  animation: CanvasBoardAnimation | null;
}): void {
  drawUnitHpBadge({ ctx, unit: unitForHpBadge({ unit, animation }), layout });
  drawUnitCargoBadge({ ctx, unit, layout });
}
